import React, { useEffect, useState } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { workspaceAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { formatDuration, formatDate, priorityConfig } from '../lib/utils';
import {
    CheckCircle2,
    Clock,
    AlertTriangle,
    TrendingUp,
    Plus,
    ArrowRight,
    ListTodo,
    Target,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function DashboardPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { 
        workspaces, 
        currentWorkspace, 
        fetchWorkspaces, 
        selectWorkspace,
        stats,
        tasks,
        spaces
    } = useWorkspace();
    const [loading, setLoading] = useState(true);
    const [recentTasks, setRecentTasks] = useState([]);
    const [isCompanyMember, setIsCompanyMember] = useState(false);

    useEffect(() => {
        initDashboard();
        checkCompanyMembership();
    }, []);

    const checkCompanyMembership = async () => {
        try {
            const API_BASE = process.env.REACT_APP_BACKEND_URL;
            const authToken = localStorage.getItem('token');
            if (!authToken) return;
            const res = await fetch(`${API_BASE}/api/companies/my`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data) setIsCompanyMember(true);
            }
        } catch (err) {}
    };

    useEffect(() => {
        initDashboard();
    }, []);

    const initDashboard = async () => {
        setLoading(true);
        try {
            const ws = await fetchWorkspaces();
            if (ws.length > 0 && !currentWorkspace) {
                await selectWorkspace(ws[0]);
            }
        } catch (error) {
            console.error('Failed to load dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Get recent tasks (last 5 updated)
        if (tasks.length > 0) {
            const sorted = [...tasks].sort((a, b) => 
                new Date(b.updated_at) - new Date(a.updated_at)
            ).slice(0, 5);
            setRecentTasks(sorted);
        }
    }, [tasks]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace) {
        return (
            <Layout>
                <div className="empty-state">
                    <div className="w-16 h-16 bg-brand-accent-soft rounded-full flex items-center justify-center mb-4">
                        <Plus className="w-8 h-8 text-brand-primary" strokeWidth={1.5} />
                    </div>
                    {isCompanyMember ? (
                        <>
                            <h2 className="empty-state-title">Belum ada workspace</h2>
                            <p className="empty-state-description mb-6">
                                Anda adalah member company. Hubungi owner company untuk ditambahkan ke workspace.
                            </p>
                        </>
                    ) : (
                        <>
                            <h2 className="empty-state-title">Create your first workspace</h2>
                            <p className="empty-state-description mb-6">
                                Workspaces help you organize your projects and collaborate with your team.
                            </p>
                            <Button 
                                onClick={() => navigate('/settings?tab=workspace')}
                                className="bg-brand-primary hover:bg-brand-primary-dark"
                                data-testid="create-first-workspace-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Create Workspace
                            </Button>
                        </>
                    )}
                </div>
            </Layout>
        );
    }

    const completionRate = stats?.completion_rate || 0;

    return (
        <Layout title={`Welcome back, ${user?.name?.split(' ')[0]}`} subtitle="Here's what's happening in your workspace">
            <div className="space-y-6 animate-fade-in" data-testid="dashboard">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="border-brand-border shadow-card" data-testid="stat-total-tasks">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary mb-1">Total Tasks</p>
                                    <p className="stat-value">{stats?.total_tasks || 0}</p>
                                </div>
                                <div className="w-10 h-10 bg-brand-accent-soft rounded-lg flex items-center justify-center">
                                    <ListTodo className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-brand-border shadow-card" data-testid="stat-completed-tasks">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary mb-1">Completed</p>
                                    <p className="stat-value text-brand-success">{stats?.completed_tasks || 0}</p>
                                </div>
                                <div className="w-10 h-10 bg-[#7ED957]/10 rounded-lg flex items-center justify-center">
                                    <CheckCircle2 className="w-5 h-5 text-brand-success" strokeWidth={1.5} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-brand-border shadow-card" data-testid="stat-overdue-tasks">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary mb-1">Overdue</p>
                                    <p className="stat-value text-brand-error">{stats?.overdue_tasks || 0}</p>
                                </div>
                                <div className="w-10 h-10 bg-[#F26B6B]/10 rounded-lg flex items-center justify-center">
                                    <AlertTriangle className="w-5 h-5 text-brand-error" strokeWidth={1.5} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-brand-border shadow-card" data-testid="stat-time-logged">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary mb-1">Time This Week</p>
                                    <p className="stat-value">{formatDuration(stats?.time_logged_this_week || 0)}</p>
                                </div>
                                <div className="w-10 h-10 bg-[#6EC1E4]/10 rounded-lg flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-brand-info" strokeWidth={1.5} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Progress & Recent Tasks */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                    {/* Progress Card */}
                    <Card className="border-brand-border shadow-card" data-testid="progress-card">
                        <CardHeader className="pb-2">
                            <CardTitle className="font-heading text-lg">Progress</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-brand-text-secondary">Completion Rate</span>
                                        <span className="text-sm font-semibold text-brand-text-primary">
                                            {completionRate.toFixed(0)}%
                                        </span>
                                    </div>
                                    <Progress value={completionRate} className="h-2" />
                                </div>
                                <div className="pt-4 border-t border-brand-border">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-brand-text-secondary">In Progress</span>
                                        <span className="font-medium text-brand-text-primary">
                                            {stats?.in_progress_tasks || 0} tasks
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Recent Tasks */}
                    <Card className="lg:col-span-2 border-brand-border shadow-card" data-testid="recent-tasks-card">
                        <CardHeader className="pb-2 flex flex-row items-center justify-between">
                            <CardTitle className="font-heading text-lg">Recent Tasks</CardTitle>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate('/tasks')}
                                className="text-brand-primary"
                                data-testid="view-all-tasks-btn"
                            >
                                View all
                                <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {recentTasks.length > 0 ? (
                                <div className="space-y-3">
                                    {recentTasks.map((task) => (
                                        <div 
                                            key={task.id}
                                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-brand-accent-soft transition-colors cursor-pointer"
                                            onClick={() => navigate(`/tasks?task=${task.id}`)}
                                            data-testid={`recent-task-${task.id}`}
                                        >
                                            <div className={`w-2 h-2 rounded-full`} style={{ backgroundColor: priorityConfig[task.priority]?.color }} />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-brand-text-primary truncate">
                                                    {task.title}
                                                </p>
                                                <p className="text-xs text-brand-text-muted">
                                                    #{task.task_number} • {task.status}
                                                </p>
                                            </div>
                                            {task.due_date && (
                                                <span className="text-xs text-brand-text-secondary">
                                                    {formatDate(task.due_date)}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-brand-text-muted">
                                    <ListTodo className="w-8 h-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                                    <p className="text-sm">No tasks yet. Create your first task!</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Spaces Overview */}
                {spaces.length > 0 && (
                    <Card className="border-brand-border shadow-card mt-6" data-testid="spaces-overview">
                        <CardHeader className="pb-2 flex flex-row items-center justify-between">
                            <CardTitle className="font-heading text-lg">Spaces</CardTitle>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate('/settings?tab=spaces')}
                                className="text-brand-primary"
                            >
                                Manage
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {spaces.slice(0, 8).map((space) => (
                                    <div 
                                        key={space.id}
                                        className="p-4 rounded-xl bg-brand-accent-soft/50 hover:bg-brand-accent-soft cursor-pointer transition-colors"
                                        onClick={() => navigate(`/tasks?space=${space.id}`)}
                                        data-testid={`space-overview-${space.id}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <div 
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: space.color }}
                                            />
                                            <span className="font-medium text-sm text-brand-text-primary truncate">
                                                {space.name}
                                            </span>
                                        </div>
                                        {space.description && (
                                            <p className="text-xs text-brand-text-muted truncate">
                                                {space.description}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
